import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import useMeasure from "react-use-measure";
import { LabelValueItem, VariableData, VariableValue } from "@/model";
import { getLabelFromValue } from "@/util/typeUtil";
import { toSpliced, withArray } from "@/util/arrayUtil";
import Button from "@/component/Button";
import { NumberInput, DropdownGroup, DropdownButton, DropdownItems, DropdownItem, TextInput } from "@/component/input";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";


const BREAKPOINT_WIDTH = 500;

type VariableScope = keyof VariableData;

type VariableItem = {
    scope: VariableScope;
    key: string;
    value: VariableValue;
}

const getNewVariableItem = (): VariableItem => ({
    scope: "global", key: "", value: { value: "", lifespan: -1 }
});

const VARIABLE_SCOPE_OPTIONS: LabelValueItem<VariableScope>[] = [
    { label: "Global", value: "global" },
    { label: "Local", value: "local" }
];

interface VariableInputGroupProps {
    required?: boolean;
    className?: string;
    data?: VariableData;
    onChange: (variable: VariableData) => void;
}

const VariableInputGroup: React.FC<VariableInputGroupProps> = (props) => {
    const { data, onChange, required, className: propsClassName } = props;
    const intl = useIntl();

    const [variables, setVariables] = useState<VariableItem[]>([]);
    const [containerRef, { width: containerWidth }] = useMeasure();

    const handleChange = (variables: VariableItem[]) => {
        setVariables(variables);

        const variableData: VariableData = { global: {}, local: {} };
        variables.forEach(({ scope, key, value }) => {
            variableData[scope]![key] = value;
        });
        onChange(variableData);
    };

    const onChangeScope = (index: number, scope: VariableScope) => {
        handleChange(withArray(variables, index, { ...variables[index], scope, value: { ...variables[index].value, lifespan: -1 } }));
    };

    const onChangeKey = (index: number, key: string) => {
        handleChange(withArray(variables, index, { ...variables[index], key }));
    };

    const onChangeValue = (index: number, value: string) => {
        handleChange(withArray(variables, index, { ...variables[index], value: { ...variables[index].value, value } }));
    };

    const onChangeLifespan = (index: number, lifespan: number) => {
        handleChange(withArray(variables, index, { ...variables[index], value: { ...variables[index].value, lifespan } }));
    };

    useEffect(() => {
        const variables = (["global", "local"] as const)
            .flatMap(scope => Object.entries(data?.[scope] ?? {})
            .map(([key, value]) => ({ scope, key, value })));

        if (variables.length === 0 && required) {
            variables.push(getNewVariableItem());
        }
        setVariables(variables);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div ref={containerRef}
             data-testid="paragraph-var-group"
             className={classNames("col-span-2 flex flex-col gap-2", propsClassName)}
        >
            <div className="flex items-center gap-2">
                <div className="field-label">
                    {intl.formatMessage({ id: "i04031" })}
                </div>
                <Button variant="text-transparent"
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        onClick={() => handleChange([...variables, getNewVariableItem()])}
                >
                    <AddIcon className="size-3" />
                </Button>
            </div>
            {required && variables.length === 0 && (
                <div role="alert"
                     className="text-invalid"
                >
                    {intl.formatMessage({ id: "i04049" })}
                </div>
            )}
            <div className="flex flex-col gap-2">
                {variables.map((variable, index) => (
                    <div key={index} className="w-full flex items-center gap-1">
                        <DropdownGroup width={containerWidth < BREAKPOINT_WIDTH ? 80 : 120}>
                            {({ open, close }) => (<>
                                <DropdownButton open={open} close={close}>
                                    {getLabelFromValue(VARIABLE_SCOPE_OPTIONS, variable.scope)}
                                </DropdownButton>
                                <DropdownItems>
                                    {VARIABLE_SCOPE_OPTIONS.map(option => (
                                        <DropdownItem key={option.value}
                                                      onClick={() => onChangeScope(index, option.value)}
                                        >
                                            {option.label}
                                        </DropdownItem>
                                    ))}
                                </DropdownItems>
                            </>)}
                        </DropdownGroup>
                        <TextInput required
                                   className="w-full"
                                   value={variable.key ?? ""}
                                   placeholder={intl.formatMessage({ id: containerWidth < BREAKPOINT_WIDTH ? "i04027_1" : "i04027" })}
                                   onChange={e => onChangeKey(index, e.target.value)}
                        />
                        <input type="text"
                               className="input-default w-full"
                               value={variable.value.value ?? ""}
                               placeholder={intl.formatMessage({ id: containerWidth < BREAKPOINT_WIDTH ? "i04028_1" : "i04028" })}
                               aria-label={intl.formatMessage({ id: "i04028_1"})}
                               onChange={e => onChangeValue(index, e.target.value)}
                        />
                        {variable.scope === "global" && (
                            <NumberInput min={-1}
                                         width={80}
                                         value={variable.value.lifespan}
                                         onChange={e => onChangeLifespan(index, Number(e.target.value))}
                            />
                        )}
                        <button disabled={required && variables.length === 0}
                                className="ml-1.5 mr-2 disabled:opacity-0 disabled:pointer-events-none"
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={() => handleChange(toSpliced(variables, index, 1))}
                        >
                            <DeleteIcon className="size-4 text-point-red" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default VariableInputGroup;
